/**
 * Fenêtre des paramètres sur ordinateur (propriétaire, 24/10/2026) : comme
 * dans les grandes applications, « Paramètres » et « Mon entreprise » s'ouvrent
 * dans une grande fenêtre par-dessus la page en cours, floutée derrière. Les
 * pages restent les mêmes et gardent leur adresse : un lien copié, un
 * rechargement ou le téléphone ouvrent la page dédiée.
 *
 * Mécanique (« page d'arrière-plan » de React Router) : le lien qui ouvre la
 * fenêtre porte dans son état la page d'où l'on vient. Tant que l'on navigue
 * dans les paramètres, la coque garde cette page en fond ; une adresse hors des
 * paramètres referme la fenêtre.
 */
import { useState } from 'react';
import type { Location } from 'react-router-dom';

/** Page ouverte par « Paramètres » et par Ctrl+, : son compte. */
export const SETTINGS_HOME = '/profil/compte';

const SETTINGS_ROOTS = ['/profil/compte', '/entreprise'] as const;

/** Adresse rendue dans la fenêtre des paramètres quand elle est ouverte. */
export function isSettingsPath(pathname: string): boolean {
  return SETTINGS_ROOTS.some((root) => pathname === root || pathname.startsWith(`${root}/`));
}

export interface SettingsOverlayState {
  settingsBackground: Pick<Location, 'pathname' | 'search' | 'hash' | 'state' | 'key'>;
}

/** État du lien qui ouvre la fenêtre : la page d'où l'on vient reste en fond. */
export function settingsLinkState(from: Location): SettingsOverlayState {
  return { settingsBackground: { pathname: from.pathname, search: from.search, hash: from.hash, state: from.state, key: from.key } };
}

/** La page de fond portée par l'état d'une adresse, si elle en porte une valide. */
export function backgroundFromState(state: unknown): SettingsOverlayState['settingsBackground'] | undefined {
  if (!state || typeof state !== 'object') return undefined;
  const background = (state as Partial<SettingsOverlayState>).settingsBackground;
  if (!background || typeof background.pathname !== 'string' || isSettingsPath(background.pathname)) return undefined;
  return background;
}

type Background = SettingsOverlayState['settingsBackground'];

/**
 * Logique pure : la page de fond après une navigation. Hors des paramètres,
 * plus de fenêtre. Au premier affichage (rechargement, lien ouvert) : la page
 * dédiée. Sinon la page portée par le lien qui a ouvert la fenêtre, ou celle
 * d'avant quand on navigue d'une page des paramètres à l'autre.
 */
export function nextSettingsBackground(
  previous: Background | undefined,
  location: Pick<Location, 'pathname' | 'state' | 'key'>,
  initialKey: string,
): Background | undefined {
  if (!isSettingsPath(location.pathname) || location.key === initialKey) return undefined;
  return backgroundFromState(location.state) ?? previous;
}

/** La page à garder en fond pendant que la fenêtre des paramètres est ouverte. */
export function useSettingsBackground(location: Location): Background | undefined {
  const [initialKey] = useState(location.key);
  const [current, setCurrent] = useState<{ key: string; background: Background | undefined }>(
    { key: location.key, background: undefined },
  );
  if (current.key === location.key) return current.background;
  // État dérivé de l'adresse, ajusté pendant le rendu (motif React documenté).
  const background = nextSettingsBackground(current.background, location, initialKey);
  setCurrent({ key: location.key, background });
  return background;
}
